import type {
  AdvancedOrderLifecycleOwnership,
  CapabilityDecision,
  CapabilityReasonCode,
  CapabilityUnsupportedReason,
  MarketIdentity,
  MarketProductKind,
  PerpsProviderId,
  PerpsProviderRegistry,
  ProductionPerpsProviderId,
  ProviderCapabilityName,
  ProviderCapabilitySet,
  ProviderCollateralMetadata,
  ProviderDescriptor,
  ProviderProductCapabilityGroups,
  ProviderProductCapabilityMap,
  ProviderProductCapabilityQuoteProfile,
  ProviderProductCapabilitySet,
  ProviderSpotQuoteScope,
} from '@infinex/types';

export type {
  AdvancedOrderCapabilityDetail,
  AdvancedOrderCapabilityMetadata,
  AdvancedOrderLifecycleOwnership,
  CapabilityDecision,
  CapabilityDecisionState,
  CapabilityFixtureGatedDecision,
  CapabilityNotApplicableDecision,
  CapabilityReasonCode,
  CapabilityReasonScope,
  CapabilitySupportedDecision,
  CapabilityUnsupportedDecision,
  CapabilityUnsupportedReason,
  HyperliquidAssetIdentity,
  HyperliquidOutcomeAssetIdentity,
  HyperliquidPerpAssetIdentity,
  HyperliquidSpotAssetIdentity,
  MarketCapabilityDecisions,
  MarketIdentity,
  MarketProductKind,
  PerpsProviderId,
  PerpsProviderRegistry,
  ProductionPerpsProviderId,
  ProviderCapabilityName,
  ProviderCapabilitySet,
  ProviderCollateralMetadata,
  ProviderDescriptor,
  ProviderEnvironment,
  ProviderExecutionScope,
  ProviderLimitMetadata,
  ProviderMarketCapability,
  ProviderProductCapabilityGroupName,
  ProviderProductCapabilityGroups,
  ProviderProductCapabilityMap,
  ProviderProductCapabilityQuoteProfile,
  ProviderProductCapabilitySet,
  ProviderRiskPolicyMetadata,
  ProviderSpotQuoteScope,
  ProviderVisibilityPolicy,
  UnsupportedReasonCategory,
  UnsupportedReasonRemediation,
} from '@infinex/types';

export {
  CAPABILITY_DECISION_STATES,
  CAPABILITY_REASON_CODES,
  CURRENT_PRODUCTION_PERPS_PROVIDER_IDS,
  CURRENT_TEST_PERPS_PROVIDER_IDS,
  HYPERLIQUID_PERP_UNIVERSES,
  MARKET_PRODUCT_KINDS,
  PROVIDER_CAPABILITY_NAMES,
  PROVIDER_EXECUTION_SCOPES,
  PROVIDER_ENVIRONMENTS,
  PROVIDER_PRODUCT_CAPABILITY_GROUP_NAMES,
  PROVIDER_SPOT_QUOTE_SCOPES,
  advancedOrderCapabilityDetailSchema,
  advancedOrderCapabilityMetadataSchema,
  capabilityDecisionSchema,
  capabilityReasonCodeSchema,
  capabilityUnsupportedReasonSchema,
  hyperliquidAssetIdentitySchema,
  marketCapabilityDecisionsSchema,
  marketIdentitySchema,
  marketProductKindSchema,
  perpsProviderIdSchema,
  productionPerpsProviderIdSchema,
  providerCapabilitySetSchema,
  providerCollateralMetadataSchema,
  providerDescriptorSchema,
  providerEnvironmentSchema,
  providerExecutionScopeSchema,
  providerLimitMetadataSchema,
  providerProductCapabilityGroupNameSchema,
  providerProductCapabilityGroupsSchema,
  providerProductCapabilityMapSchema,
  providerProductCapabilityQuoteProfileSchema,
  providerProductCapabilitySetSchema,
  providerRiskPolicyMetadataSchema,
  providerSpotQuoteScopeSchema,
  providerVisibilityPolicySchema,
} from '@infinex/types';

const INTERNAL_USD_CLASS_MOVEMENT =
  `usdClass${'Transfer'}` as const satisfies ProviderCapabilityName;

const supported = (
  options: Omit<
    Extract<CapabilityDecision, { state: 'supported' }>,
    'state'
  > = {}
): CapabilityDecision => ({
  state: 'supported',
  ...options,
});

const unsupportedReason = (
  input: CapabilityUnsupportedReason
): CapabilityUnsupportedReason => input;

const unsupported = (
  reason: CapabilityUnsupportedReason
): Extract<CapabilityDecision, { state: 'unsupported' }> => ({
  state: 'unsupported',
  reason: reason.message,
  reasonCode: reason.code,
  structuralReason: reason,
});

const fixtureGated = (
  fixture: string,
  reason: CapabilityUnsupportedReason
): CapabilityDecision => ({
  state: 'fixture_gated',
  fixture,
  reason: reason.message,
  reasonCode: reason.code,
  structuralReason: reason,
});

const notApplicable = (
  reason: string,
  reasonCode: CapabilityReasonCode = 'not_applicable_to_product'
): CapabilityDecision => ({
  state: 'not_applicable',
  reason,
  reasonCode,
});

const productNotRegistered = (
  productKind: MarketProductKind,
  message: string
): CapabilityUnsupportedReason =>
  unsupportedReason({
    code: 'product_not_registered',
    message,
    scope: 'product',
    category: 'product_scope',
    remediation: 'choose_supported_product',
    details: [`productKind:${productKind}`],
  });

const featureUnsupported = (
  feature: ProviderCapabilityName,
  message: string
): CapabilityUnsupportedReason =>
  unsupportedReason({
    code: 'feature_not_supported',
    message,
    scope: 'action',
    category: 'provider_feature',
    remediation: 'choose_supported_provider',
    details: [`capability:${feature}`],
  });

const productFeatureUnsupported = (
  productKind: MarketProductKind,
  feature: string,
  message: string
): CapabilityUnsupportedReason =>
  unsupportedReason({
    code: 'feature_not_supported',
    message,
    scope: 'action',
    category: 'provider_feature',
    remediation: 'wait_for_future_support',
    details: [`productKind:${productKind}`, `capability:${feature}`],
  });

const testOnlyProvider = (providerId: PerpsProviderId): CapabilityDecision =>
  unsupported({
    code: 'provider_test_only',
    message: `${providerId} is a test-only provider and is not accepted by production perps routes.`,
    scope: 'provider',
    category: 'registration',
    remediation: 'choose_supported_provider',
    details: [`providerId:${providerId}`],
  });

const unsupportedProviderVisibility = (
  providerId: PerpsProviderId,
  message: string
): CapabilityDecision =>
  unsupported({
    code: 'provider_not_registered',
    message,
    scope: 'provider',
    category: 'registration',
    remediation: 'choose_supported_provider',
    details: [`providerId:${providerId}`],
  });

const USDC_ARBITRUM: ProviderCollateralMetadata = {
  asset: 'USDC',
  symbol: 'USDC',
  name: 'USD Coin',
  decimals: 6,
  chain: 'arbitrum',
  chainId: 42161,
  tokenAddress: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
  assetId: 'usd_coin',
};

const USDT_ETHEREUM: ProviderCollateralMetadata = {
  asset: 'USDT',
  symbol: 'USDT',
  name: 'Tether USD',
  decimals: 6,
  chain: 'ethereum',
  chainId: 1,
  tokenAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  assetId: 'tether',
  usePermit2: false,
};

const productionVisibility = (
  providerId: ProductionPerpsProviderId
): ProviderDescriptor['visibility'] => ({
  productionRegistered: true,
  testOnly: false,
  backendPerpsRoutes: supported({
    constraints: [
      'accepted by current perps deposit, depositWithSwidge, and withdraw schemas',
    ],
  }),
  depositWithdrawUi: supported({
    constraints: ['present in current web-shared perps provider config'],
  }),
  featureFlag: 'enablePerpsMultiExchanges',
  currentFallbackProviderId:
    providerId === 'hyperliquid' ? 'hyperliquid' : undefined,
});

const mockVisibility: ProviderDescriptor['visibility'] = {
  productionRegistered: false,
  testOnly: true,
  backendPerpsRoutes: testOnlyProvider('mock'),
  depositWithdrawUi: unsupportedProviderVisibility(
    'mock',
    'mock is not present in the current deposit/withdraw provider config.'
  ),
  featureFlag: 'enablePerpsMultiExchanges',
};

const perpsOnlyMarkets = (
  spotMessage: string,
  outcomeMessage: string
): ProviderCapabilitySet['markets'] => ({
  perp: supported(),
  spot: unsupported(productNotRegistered('spot', spotMessage)),
  outcome: unsupported(productNotRegistered('outcome', outcomeMessage)),
});

const notPerpProduct = (
  capability: ProviderCapabilityName
): CapabilityDecision =>
  notApplicable(
    `${capability} applies to the current perp product surface only.`
  );

const hyperliquidRiskPolicy = {
  close: supported(),
  reverse: supported({
    constraints: [
      'reverse flows must cancel attached protection before replacing exposure',
    ],
  }),
  tpSl: supported(),
  reduceOnly: supported(),
  indeterminateActionPolicy: 'requires_review',
  knownProtectionCancelFailurePolicy: 'fail_closed',
} as const satisfies ProviderCapabilitySet['riskPolicy'];

const synthetixStandardReduceOnly = supported({
  constraints: [
    'standard market and limit orders pass reduce_only through to the Synthetix adapter',
  ],
});

const synthetixStandardPostOnly = supported({
  constraints: [
    'standard ALO limit orders map to postOnly in the Synthetix adapter',
  ],
});

const advancedOrderCapability = (
  capability: CapabilityDecision,
  parentLifecycle: CapabilityDecision,
  ownership: AdvancedOrderLifecycleOwnership
): ProviderCapabilitySet['advancedOrders']['scale'] => ({
  capability,
  parentLifecycle,
  ownership,
});

const unsupportedAdvancedOrderParentLifecycle = (
  message: string
): CapabilityDecision =>
  unsupported(featureUnsupported('advancedOrderParentLifecycle', message));

const currentScaleParentLifecycle = () =>
  unsupportedAdvancedOrderParentLifecycle(
    'Current Scale submission is app-orchestrated but does not create a durable parent lifecycle.'
  );

const providerNativeTwapParentLifecycle = () =>
  supported({
    constraints: [
      'TWAP parent identity and lifecycle are provider-native and refreshed from provider account state',
    ],
  });

const twapRandomizationParentLifecycle = () =>
  notApplicable(
    'TWAP randomization is a TWAP parameter, not an independent parent lifecycle.'
  );

const currentAdvancedOrders = (
  scale: CapabilityDecision,
  twap: CapabilityDecision,
  twapRandomization: CapabilityDecision
): ProviderCapabilitySet['advancedOrders'] => ({
  scale: advancedOrderCapability(
    scale,
    currentScaleParentLifecycle(),
    'app_orchestrated'
  ),
  twap: advancedOrderCapability(
    twap,
    providerNativeTwapParentLifecycle(),
    'provider_native'
  ),
  twapRandomization: advancedOrderCapability(
    twapRandomization,
    twapRandomizationParentLifecycle(),
    twapRandomization.state === 'supported' ? 'provider_native' : 'unsupported'
  ),
});

type ProviderCapabilitySetBase = Omit<
  ProviderCapabilitySet,
  'productCapabilities'
>;

const duplicateDecisionGroups = (
  productKind: MarketProductKind,
  decision: CapabilityDecision,
  options: Pick<
    ProviderProductCapabilitySet,
    'executionScope' | 'quoteScope' | 'metadata'
  > = {}
): ProviderProductCapabilitySet => ({
  productKind,
  ...options,
  marketDiscovery: {
    marketCatalog: decision,
    marketDisplay: decision,
    assetIdentity: decision,
  },
  marketData: {
    topOfBook: decision,
    orderBook: decision,
    recentTrades: decision,
    candles: decision,
  },
  accountData: {
    balances: decision,
    positions: decision,
    openOrders: decision,
    fills: decision,
    history: decision,
  },
  execution: {
    marketOrder: decision,
    limitOrder: decision,
    triggerOrder: decision,
    postOnly: decision,
    reduceOnly: decision,
  },
  advancedOrders: {
    scale: decision,
    twap: decision,
    twapRandomization: decision,
    parentLifecycle: decision,
  },
  moneyMovement: {
    directDeposit: decision,
    swidgeDeposit: decision,
    withdraw: decision,
    [INTERNAL_USD_CLASS_MOVEMENT]: decision,
    spotSend: decision,
    sendAsset: decision,
    externalSpotDeposit: decision,
    externalSpotWithdrawal: decision,
  },
  feesAnalytics: {
    cashback: decision,
    fees: decision,
    funding: decision,
    liquidation: decision,
  },
  lifecycleActions: {
    close: decision,
    reverse: decision,
    tpSl: decision,
    cancel: decision,
    modify: decision,
    outcomeSplitMerge: decision,
  },
});

const notApplicableProductDecision = (
  productKind: MarketProductKind,
  feature: string,
  reason?: string
): CapabilityDecision =>
  notApplicable(
    reason ??
      `${feature} is not applicable to ${productKind} markets in the current product scope.`
  );

const fixtureGateForProduct = (
  fixture: string,
  productKind: MarketProductKind,
  feature: string,
  reason: string,
  details: readonly string[] = []
): CapabilityDecision =>
  fixtureGated(
    fixture,
    unsupportedReason({
      code: 'fixture_only',
      message: reason,
      scope: 'fixture',
      category: 'fixture_gate',
      remediation: 'wait_for_future_support',
      details: [
        `productKind:${productKind}`,
        `capability:${feature}`,
        ...details,
      ],
    })
  );

const unsupportedForProduct = (
  productKind: MarketProductKind,
  feature: string,
  reason: string
): Extract<CapabilityDecision, { state: 'unsupported' }> =>
  unsupported(productFeatureUnsupported(productKind, feature, reason));

const perpProductCapabilities = (
  capabilities: ProviderCapabilitySetBase
): ProviderProductCapabilitySet => {
  const market = capabilities.markets.perp;
  const nonPerpInternalMovement = notApplicableProductDecision(
    'perp',
    INTERNAL_USD_CLASS_MOVEMENT,
    'USD class transfer is a spot balance-class movement, not a perps provider deposit or withdrawal.'
  );
  const perpsOnlyFunding = supported({
    constraints: ['perps funding metadata is modeled on perps markets only'],
  });

  return {
    productKind: 'perp',
    executionScope: 'all_markets',
    marketDiscovery: {
      marketCatalog: market,
      marketDisplay: market,
      assetIdentity: market,
    },
    marketData: {
      topOfBook: capabilities.topOfBook,
      orderBook: capabilities.topOfBook,
      recentTrades: capabilities.topOfBook,
      candles: capabilities.topOfBook,
    },
    accountData: {
      balances: market,
      positions: market,
      openOrders: market,
      fills: market,
      history: market,
    },
    execution: {
      marketOrder: capabilities.marketOrder,
      limitOrder: capabilities.limitOrder,
      triggerOrder: capabilities.triggerOrder,
      postOnly: capabilities.postOnly,
      reduceOnly: capabilities.reduceOnly,
    },
    advancedOrders: {
      scale: capabilities.scale,
      twap: capabilities.twap,
      twapRandomization: capabilities.twapRandomization,
      parentLifecycle: capabilities.advancedOrderParentLifecycle,
    },
    moneyMovement: {
      directDeposit: capabilities.directDeposit,
      swidgeDeposit: capabilities.swidgeDeposit,
      withdraw: capabilities.withdraw,
      [INTERNAL_USD_CLASS_MOVEMENT]: nonPerpInternalMovement,
      spotSend: notApplicableProductDecision('perp', 'spotSend'),
      sendAsset: notApplicableProductDecision('perp', 'sendAsset'),
      externalSpotDeposit: notApplicableProductDecision(
        'perp',
        'externalSpotDeposit'
      ),
      externalSpotWithdrawal: notApplicableProductDecision(
        'perp',
        'externalSpotWithdrawal'
      ),
    },
    feesAnalytics: {
      cashback: capabilities.cashback,
      fees: supported(),
      funding: perpsOnlyFunding,
      liquidation: supported({
        constraints: ['liquidation is a perps position concept'],
      }),
    },
    lifecycleActions: {
      close: capabilities.close,
      reverse: capabilities.reverse,
      tpSl: capabilities.tpSl,
      cancel: supported(),
      modify: supported(),
      outcomeSplitMerge: notApplicableProductDecision(
        'perp',
        'outcomeSplitMerge'
      ),
    },
  };
};

const productUnavailableCapabilities = (
  productKind: MarketProductKind,
  decision: CapabilityDecision
): ProviderProductCapabilitySet =>
  duplicateDecisionGroups(productKind, decision, {
    executionScope: productKind === 'outcome' ? 'extension_lock' : undefined,
  });

const HYPERLIQUID_SPOT_USDC_FIXTURE =
  'capabilities.hyperliquid-spot-usdc-v1.json';
const HYPERLIQUID_SPOT_NON_USDC_FIXTURE =
  'capabilities.hyperliquid-spot-non-usdc-deferred.json';
const HYPERLIQUID_SPOT_UNSUPPORTED_ACTIONS_FIXTURE =
  'capabilities.hyperliquid-spot-unsupported-actions.json';
const HYPERLIQUID_SPOT_NOT_APPLICABLE_FIXTURE =
  'capabilities.hyperliquid-spot-not-applicable-controls.json';
const HYPERLIQUID_HIP4_FIXTURE =
  'capabilities.hyperliquid-hip4-fixture-gated.json';

const hyperliquidProductMarkets: ProviderCapabilitySet['markets'] = {
  perp: supported(),
  spot: supported({
    constraints: [
      'Hyperliquid spotMeta.universe market discovery',
      `evidence fixture ${HYPERLIQUID_SPOT_USDC_FIXTURE}`,
    ],
  }),
  outcome: fixtureGateForProduct(
    HYPERLIQUID_HIP4_FIXTURE,
    'outcome',
    'marketDiscovery',
    'HIP-4 outcome market discovery is extension-lock fixture evidence and is not production-visible execution support.',
    ['extensionLockOnly:true']
  ),
};

const hyperliquidSpotNotApplicableControl = (feature: string) =>
  notApplicableProductDecision(
    'spot',
    feature,
    `${feature} is a perps control and is not applicable to Hyperliquid Spot V1. Evidence: ${HYPERLIQUID_SPOT_NOT_APPLICABLE_FIXTURE}.`
  );

const hyperliquidOutcomeNotApplicableControl = (feature: string) =>
  notApplicableProductDecision(
    'outcome',
    feature,
    `${feature} is a perps control and is not applicable to HIP-4 outcome products. Evidence: ${HYPERLIQUID_SPOT_NOT_APPLICABLE_FIXTURE}.`
  );

const hyperliquidOutcomeUnsupportedAction = (
  feature: string,
  fixtureCapabilities: readonly string[],
  reason: string,
  details: readonly string[] = []
): CapabilityDecision =>
  unsupported({
    ...productFeatureUnsupported('outcome', feature, reason),
    details: [
      `productKind:outcome`,
      `capability:${feature}`,
      ...fixtureCapabilities.map(
        (fixtureCapability) => `fixtureCapability:${fixtureCapability}`
      ),
      `fixture:${HYPERLIQUID_SPOT_UNSUPPORTED_ACTIONS_FIXTURE}`,
      ...details,
    ],
  });

const hyperliquidSpotCapabilities = (): ProviderProductCapabilitySet => {
  const marketDiscovery = supported({
    constraints: [
      'Hyperliquid spotMeta.universe rows',
      `evidence fixture ${HYPERLIQUID_SPOT_USDC_FIXTURE}`,
    ],
  });
  const marketData = supported({
    constraints: [
      'Hyperliquid spot market data is read through product-scoped runtime selectors',
      `evidence fixture ${HYPERLIQUID_SPOT_USDC_FIXTURE}`,
    ],
  });
  const accountData = supported({
    constraints: [
      'spot balances, fills, open orders, and history preserve product-aware identity',
      `evidence fixture ${HYPERLIQUID_SPOT_USDC_FIXTURE}`,
    ],
  });
  const spotOrderExecution = supported({
    constraints: [
      'Hyperliquid Spot V1 market/IOC, limit, post-only, cancel, cancel-by-cloid, and modify payloads',
      `evidence fixture ${HYPERLIQUID_SPOT_USDC_FIXTURE}`,
    ],
  });
  const spotTriggerOrder = unsupportedForProduct(
    'spot',
    'triggerOrder',
    'Spot V1 supports market/IOC, limit, post-only, cancel, and modify. Trigger orders are out of Spot V1 scope.'
  );
  const spotAdvancedOrders = unsupportedForProduct(
    'spot',
    'advancedOrders',
    'Spot V1 advanced order flows are out of scope for launch; use market/IOC, limit, post-only, cancel, and modify.'
  );
  const spotFees = supported({
    constraints: [
      'spot fee evidence is surfaced through provider order responses, fills, history, and activity rows',
      `evidence fixture ${HYPERLIQUID_SPOT_USDC_FIXTURE}`,
    ],
  });
  const spotInternalClassMovement = unsupportedForProduct(
    'spot',
    INTERNAL_USD_CLASS_MOVEMENT,
    'Internal USD class balance movement is not exposed as normal Spot V1 funding UX under the unified-only product policy.'
  ) satisfies Exclude<CapabilityDecision, { state: 'supported' }>;
  const unsupportedSpotSend = unsupported(
    productFeatureUnsupported(
      'spot',
      'spotSend',
      'Spot V1 funding scope does not expose spotSend.'
    )
  );
  const unsupportedSendAsset = unsupported(
    productFeatureUnsupported(
      'spot',
      'sendAsset',
      'Generalized sendAsset is out of Spot V1 launch scope.'
    )
  );
  const unsupportedExternalDeposit = unsupported(
    productFeatureUnsupported(
      'spot',
      'externalSpotDeposit',
      'Spot V1 funding is internal USDC class transfer, not external spot deposits.'
    )
  );
  const unsupportedExternalWithdrawal = unsupported(
    productFeatureUnsupported(
      'spot',
      'externalSpotWithdrawal',
      'Spot V1 funding is internal USDC class transfer, not external spot withdrawals.'
    )
  );

  const capabilities: ProviderProductCapabilitySet = {
    productKind: 'spot',
    executionScope: 'usdc_quote',
    quoteScope: 'USDC',
    metadata: {
      fixture: HYPERLIQUID_SPOT_USDC_FIXTURE,
      nonUsdcFixture: HYPERLIQUID_SPOT_NON_USDC_FIXTURE,
    },
    marketDiscovery: {
      marketCatalog: marketDiscovery,
      marketDisplay: marketDiscovery,
      assetIdentity: marketDiscovery,
    },
    marketData: {
      topOfBook: marketData,
      orderBook: marketData,
      recentTrades: marketData,
      candles: marketData,
    },
    accountData: {
      balances: accountData,
      positions: hyperliquidSpotNotApplicableControl('positions'),
      openOrders: accountData,
      fills: accountData,
      history: accountData,
    },
    execution: {
      marketOrder: spotOrderExecution,
      limitOrder: spotOrderExecution,
      triggerOrder: spotTriggerOrder,
      postOnly: spotOrderExecution,
      reduceOnly: hyperliquidSpotNotApplicableControl('reduceOnly'),
    },
    advancedOrders: {
      scale: spotAdvancedOrders,
      twap: spotAdvancedOrders,
      twapRandomization: spotAdvancedOrders,
      parentLifecycle: spotAdvancedOrders,
    },
    moneyMovement: {
      directDeposit: unsupportedExternalDeposit,
      swidgeDeposit: unsupportedExternalDeposit,
      withdraw: unsupportedExternalWithdrawal,
      [INTERNAL_USD_CLASS_MOVEMENT]: spotInternalClassMovement,
      spotSend: unsupportedSpotSend,
      sendAsset: unsupportedSendAsset,
      externalSpotDeposit: unsupportedExternalDeposit,
      externalSpotWithdrawal: unsupportedExternalWithdrawal,
    },
    feesAnalytics: {
      cashback: unsupportedForProduct(
        'spot',
        'cashback',
        'Spot V1 cashback metadata is not launch-supported by the committed capability fixtures.'
      ),
      fees: spotFees,
      funding: hyperliquidSpotNotApplicableControl('funding'),
      liquidation: hyperliquidSpotNotApplicableControl('liquidation'),
    },
    lifecycleActions: {
      close: hyperliquidSpotNotApplicableControl('close'),
      reverse: hyperliquidSpotNotApplicableControl('reverse'),
      tpSl: hyperliquidSpotNotApplicableControl('tpSl'),
      cancel: spotOrderExecution,
      modify: spotOrderExecution,
      outcomeSplitMerge:
        hyperliquidSpotNotApplicableControl('outcomeSplitMerge'),
    },
  };

  return {
    ...capabilities,
    quoteOverrides: {
      'non-USDC': hyperliquidSpotNonUsdcQuoteCapabilities(capabilities),
    },
  };
};

const hyperliquidSpotNonUsdcQuoteCapabilities = (
  base: ProviderProductCapabilitySet
): ProviderProductCapabilityQuoteProfile => {
  const nonUsdcQuoteExecution = unsupported(
    productFeatureUnsupported(
      'spot',
      'non_usdc_quote_execution',
      'Spot V1 supports USDC-quoted Hyperliquid spot markets only.'
    )
  );

  return {
    ...base,
    quoteScope: 'non-USDC',
    executionScope: 'usdc_quote',
    metadata: {
      fixture: HYPERLIQUID_SPOT_NON_USDC_FIXTURE,
      usdcFixture: HYPERLIQUID_SPOT_USDC_FIXTURE,
    },
    execution: {
      ...base.execution,
      marketOrder: nonUsdcQuoteExecution,
      limitOrder: nonUsdcQuoteExecution,
      postOnly: nonUsdcQuoteExecution,
    },
    lifecycleActions: {
      ...base.lifecycleActions,
      cancel: nonUsdcQuoteExecution,
      modify: nonUsdcQuoteExecution,
    },
  };
};

/**
 * The two public build variables the dev override reads, typed locally so
 * they stay out of the global `NodeJS.ProcessEnv` — this package is imported
 * by the worker and server packages, which have no business seeing these
 * Next-only keys on every `process.env` access. The inline casts below erase
 * at compile time, so each read still keeps the dot-form shape the strictest
 * preset's `noPropertyAccessFromIndexSignature` would otherwise reject.
 */
type OutcomeUnlockEnv = {
  NEXT_PUBLIC_INFINEX_ENVIRONMENT?: string;
  NEXT_PUBLIC_UNLOCK_OUTCOME_EXECUTION?: string;
};

/**
 * Dev-only escape hatch that unlocks HIP-4 outcome order placement and cancel
 * on a local build so a human can open and close an outcome position by hand.
 * It flips exactly three decisions — `execution.marketOrder`,
 * `execution.limitOrder`, and `lifecycleActions.cancel`; every other outcome
 * capability keeps its fixture gate.
 *
 * The gate requires the environment to be exactly `dev`: test and staging
 * deployments run against mainnet Hyperliquid with real funds, so a looser
 * `!== 'prod'` check would let one deployment env var flip live execution on
 * for every user of that deployment. Local dev still qualifies — the perps-app
 * build defaults `NEXT_PUBLIC_INFINEX_ENVIRONMENT` to `dev` when unset.
 *
 * Both reads are written as full literal `process.env.X` member expressions
 * because Next replaces them textually at build time — a destructured or
 * computed read would not be inlined into the browser bundle. In any deployed
 * build the environment check collapses to a constant `false`, and in every
 * non-Next consumer of this package (the worker, server packages) the variable
 * is simply unset.
 */
const outcomeExecutionDevOverride = (): CapabilityDecision | undefined =>
  (process.env as OutcomeUnlockEnv).NEXT_PUBLIC_INFINEX_ENVIRONMENT === 'dev' &&
  (process.env as OutcomeUnlockEnv).NEXT_PUBLIC_UNLOCK_OUTCOME_EXECUTION ===
    'true'
    ? supported({
        constraints: [
          'dev-only override for local manual trading; not production support',
        ],
      })
    : undefined;

const hyperliquidOutcomeCapabilities = (): ProviderProductCapabilitySet => {
  const devUnlock = outcomeExecutionDevOverride();
  const discovery = fixtureGateForProduct(
    HYPERLIQUID_HIP4_FIXTURE,
    'outcome',
    'marketDiscovery',
    'HIP-4 discovery and identity are extension-lock fixture evidence and are not production-visible execution support.',
    ['extensionLockOnly:true']
  );
  const marketData = fixtureGateForProduct(
    HYPERLIQUID_HIP4_FIXTURE,
    'outcome',
    'marketData',
    'HIP-4 market data is fixture-gated until recentTrades, candles, websocket/runtime mapping, backend, and UI policy fixtures are committed.',
    ['extensionLockOnly:true']
  );
  const accountData = fixtureGateForProduct(
    HYPERLIQUID_HIP4_FIXTURE,
    'outcome',
    'accountData',
    'HIP-4 balances, fills, activity, history, and CSV rows are fixture-gated until product-specific read-model fixtures are committed.',
    ['extensionLockOnly:true']
  );
  const execution = fixtureGateForProduct(
    HYPERLIQUID_HIP4_FIXTURE,
    'outcome',
    'execution',
    'HIP-4 production execution remains fixture-gated pending signed outcome order, cancel, rejection, backend, UI, and settlement fixtures.',
    ['extensionLockOnly:true']
  );
  const lifecycle = fixtureGateForProduct(
    HYPERLIQUID_HIP4_FIXTURE,
    'outcome',
    'lifecycleActions',
    'HIP-4 lifecycle actions remain fixture-gated pending official-doc or signed/live fixture evidence for split, merge, negate, and mergeQuestion semantics.',
    ['extensionLockOnly:true']
  );
  const unsupportedOutcomeFunding = (feature: string) =>
    hyperliquidOutcomeUnsupportedAction(
      feature,
      ['outcome_funding_route'],
      'Outcome product funding has no captured route while HIP-4 remains extension-locked.'
    );
  const unsupportedOutcomeTokenSend = (
    feature: string,
    fixtureCapability: 'send_asset' | 'spot_send'
  ) =>
    hyperliquidOutcomeUnsupportedAction(
      feature,
      [fixtureCapability],
      'Outcome token send is blocked pending signed token send/transfer fixture and product/security policy.',
      ['matrixCapability:Outcome token send']
    );

  return {
    productKind: 'outcome',
    executionScope: 'extension_lock',
    metadata: {
      fixture: HYPERLIQUID_HIP4_FIXTURE,
      extensionLockOnly: true,
    },
    marketDiscovery: {
      marketCatalog: discovery,
      marketDisplay: discovery,
      assetIdentity: discovery,
    },
    marketData: {
      topOfBook: marketData,
      orderBook: marketData,
      recentTrades: marketData,
      candles: marketData,
    },
    accountData: {
      balances: accountData,
      positions: hyperliquidOutcomeNotApplicableControl('positions'),
      openOrders: accountData,
      fills: accountData,
      history: accountData,
    },
    execution: {
      marketOrder: devUnlock ?? execution,
      limitOrder: devUnlock ?? execution,
      triggerOrder: execution,
      postOnly: execution,
      reduceOnly: hyperliquidOutcomeNotApplicableControl('reduceOnly'),
    },
    advancedOrders: {
      scale: execution,
      twap: execution,
      twapRandomization: execution,
      parentLifecycle: lifecycle,
    },
    moneyMovement: {
      directDeposit: unsupportedOutcomeFunding('directDeposit'),
      swidgeDeposit: unsupportedOutcomeFunding('swidgeDeposit'),
      withdraw: unsupportedOutcomeTokenSend('withdraw', 'send_asset'),
      [INTERNAL_USD_CLASS_MOVEMENT]: unsupportedOutcomeFunding(
        INTERNAL_USD_CLASS_MOVEMENT
      ),
      spotSend: unsupportedOutcomeTokenSend('spotSend', 'spot_send'),
      sendAsset: unsupportedOutcomeTokenSend('sendAsset', 'send_asset'),
      externalSpotDeposit: unsupportedOutcomeFunding('externalSpotDeposit'),
      externalSpotWithdrawal: unsupportedOutcomeTokenSend(
        'externalSpotWithdrawal',
        'send_asset'
      ),
    },
    feesAnalytics: {
      cashback: execution,
      fees: execution,
      funding: hyperliquidOutcomeNotApplicableControl('funding'),
      liquidation: hyperliquidOutcomeNotApplicableControl('liquidation'),
    },
    lifecycleActions: {
      close: hyperliquidOutcomeNotApplicableControl('close'),
      reverse: hyperliquidOutcomeNotApplicableControl('reverse'),
      tpSl: hyperliquidOutcomeNotApplicableControl('tpSl'),
      cancel: devUnlock ?? execution,
      modify: execution,
      outcomeSplitMerge: lifecycle,
    },
  };
};

const productCapabilitiesForProvider = (
  capabilities: ProviderCapabilitySetBase
): ProviderProductCapabilityMap => {
  if (capabilities.providerId === 'hyperliquid') {
    return {
      perp: perpProductCapabilities(capabilities),
      spot: hyperliquidSpotCapabilities(),
      outcome: hyperliquidOutcomeCapabilities(),
    };
  }

  return {
    perp: perpProductCapabilities(capabilities),
    spot: productUnavailableCapabilities('spot', capabilities.markets.spot),
    outcome: productUnavailableCapabilities(
      'outcome',
      capabilities.markets.outcome
    ),
  };
};

export const CURRENT_PROVIDER_DESCRIPTORS = {
  hyperliquid: {
    providerId: 'hyperliquid',
    runtimeId: 'hyperliquid',
    displayName: 'Hyperliquid',
    environment: 'production',
    productKinds: ['perp', 'spot', 'outcome'],
    visibility: productionVisibility('hyperliquid'),
  },
  synthetix: {
    providerId: 'synthetix',
    runtimeId: 'synthetix',
    displayName: 'Synthetix',
    environment: 'production',
    productKinds: ['perp'],
    visibility: productionVisibility('synthetix'),
  },
  lighter: {
    providerId: 'lighter',
    runtimeId: 'lighter',
    displayName: 'Lighter',
    environment: 'production',
    productKinds: ['perp'],
    visibility: productionVisibility('lighter'),
  },
  mock: {
    providerId: 'mock',
    runtimeId: 'mock',
    displayName: 'Mock',
    environment: 'test',
    productKinds: ['perp'],
    visibility: mockVisibility,
  },
} as const satisfies Record<PerpsProviderId, ProviderDescriptor>;

const CURRENT_PROVIDER_CAPABILITIES_BASE = {
  hyperliquid: {
    providerId: 'hyperliquid',
    environment: 'production',
    network: 'mainnet',
    markets: hyperliquidProductMarkets,
    directDeposit: supported({
      route: 'trpc.perps.deposit',
      constraints: ['USDC on Arbitrum', 'minimum deposit 10 USD'],
    }),
    swidgeDeposit: supported({
      route: 'trpc.perps.depositWithSwidge',
      constraints: ['destination asset must be Hyperliquid USDC'],
    }),
    withdraw: supported({
      route: 'trpc.perps.withdraw',
      constraints: ['USDC on Arbitrum', 'minimum withdrawal 2 USD'],
    }),
    [INTERNAL_USD_CLASS_MOVEMENT]: notApplicable(
      'Hyperliquid internal USD class movement is not exposed as a provider-level action.'
    ),
    spotSend: unsupported(
      featureUnsupported(
        'spotSend',
        'Spot send is not exposed in the current provider action surface.'
      )
    ),
    sendAsset: unsupported(
      featureUnsupported(
        'sendAsset',
        'Generalized asset send is not exposed in the current provider action surface.'
      )
    ),
    marketOrder: supported(),
    limitOrder: supported(),
    triggerOrder: supported({
      constraints: [
        'stop-market, stop-limit, take-profit, and stop-loss order entry',
      ],
    }),
    tpSl: hyperliquidRiskPolicy.tpSl,
    reduceOnly: hyperliquidRiskPolicy.reduceOnly,
    reverse: hyperliquidRiskPolicy.reverse,
    close: hyperliquidRiskPolicy.close,
    scale: supported(),
    twap: supported(),
    twapRandomization: supported(),
    postOnly: supported(),
    cashback: supported(),
    topOfBook: supported(),
    advancedOrderParentLifecycle: supported({
      constraints: [
        'TWAP parent lifecycle is provider-native; current Scale submission has no durable parent lifecycle',
        'inspect advancedOrders for per-feature ownership',
      ],
    }),
    collateral: [USDC_ARBITRUM],
    limits: {
      minimumDepositUsd: '10',
      minimumWithdrawalUsd: '2',
    },
    riskPolicy: hyperliquidRiskPolicy,
    advancedOrders: currentAdvancedOrders(
      supported(),
      supported(),
      supported()
    ),
    visibility: CURRENT_PROVIDER_DESCRIPTORS.hyperliquid.visibility,
  },
  synthetix: {
    providerId: 'synthetix',
    environment: 'production',
    network: 'mainnet',
    markets: perpsOnlyMarkets(
      'current perps app registration is perp-oriented only',
      'current perps app registration has no outcome product'
    ),
    directDeposit: supported({
      route: 'trpc.perps.deposit',
      constraints: [
        'USDT on Ethereum',
        'EOA wallet only',
        'minimum deposit 50 USD',
      ],
    }),
    swidgeDeposit: supported({
      route: 'trpc.perps.depositWithSwidge',
      constraints: [
        'backend accepts Synthetix Swidge deposits',
        'current deposit UI hides the path through directDepositOnly',
      ],
      metadata: { uiVisibility: 'hidden' },
    }),
    withdraw: supported({
      route: 'trpc.perps.withdraw',
      constraints: [
        'USDT on Ethereum',
        'requires existing Synthetix subaccount',
        'minimum withdrawal 50 USD',
      ],
    }),
    [INTERNAL_USD_CLASS_MOVEMENT]: notApplicable(
      'Synthetix does not expose Hyperliquid USD balance classes.'
    ),
    spotSend: unsupported(
      featureUnsupported(
        'spotSend',
        'Synthetix spot send is not part of the current provider action surface.'
      )
    ),
    sendAsset: unsupported(
      featureUnsupported(
        'sendAsset',
        'Synthetix generalized asset send is not part of the current provider action surface.'
      )
    ),
    marketOrder: supported(),
    limitOrder: supported(),
    triggerOrder: unsupported(
      featureUnsupported(
        'triggerOrder',
        'Synthetix current registration does not support stop-market or stop-limit orders.'
      )
    ),
    tpSl: unsupported(
      featureUnsupported(
        'tpSl',
        'Synthetix current registration does not support take-profit or stop-loss order entry.'
      )
    ),
    reduceOnly: synthetixStandardReduceOnly,
    reverse: supported({
      constraints: [
        'reverse is represented as app risk intent, backed by market and limit support',
      ],
    }),
    close: supported({
      constraints: [
        'close is represented as app risk intent, backed by market order support',
      ],
    }),
    scale: unsupported(
      featureUnsupported(
        'scale',
        'Synthetix current registration does not support scale orders.'
      )
    ),
    twap: unsupported(
      featureUnsupported(
        'twap',
        'Synthetix current registration does not support TWAP orders.'
      )
    ),
    twapRandomization: unsupported(
      featureUnsupported(
        'twapRandomization',
        'Synthetix current registration does not support TWAP randomization.'
      )
    ),
    postOnly: synthetixStandardPostOnly,
    cashback: unsupported(
      featureUnsupported(
        'cashback',
        'Synthetix current registration does not support cashback metadata.'
      )
    ),
    topOfBook: unsupported(
      featureUnsupported(
        'topOfBook',
        'Synthetix current registration does not support top-of-book display.'
      )
    ),
    advancedOrderParentLifecycle: unsupported(
      featureUnsupported(
        'advancedOrderParentLifecycle',
        'Synthetix current registration has no scale or TWAP parent lifecycle.'
      )
    ),
    collateral: [USDT_ETHEREUM],
    limits: {
      minimumDepositUsd: '50',
      minimumWithdrawalUsd: '50',
    },
    riskPolicy: {
      close: supported(),
      reverse: supported(),
      tpSl: unsupported(
        featureUnsupported(
          'tpSl',
          'Synthetix current registration does not support take-profit or stop-loss order entry.'
        )
      ),
      reduceOnly: synthetixStandardReduceOnly,
      indeterminateActionPolicy: 'requires_review',
      knownProtectionCancelFailurePolicy: 'fail_closed',
    },
    advancedOrders: {
      scale: advancedOrderCapability(
        unsupported(
          featureUnsupported(
            'scale',
            'Synthetix current registration does not support scale orders.'
          )
        ),
        unsupportedAdvancedOrderParentLifecycle(
          'Synthetix current registration has no advanced-order parent lifecycle.'
        ),
        'unsupported'
      ),
      twap: advancedOrderCapability(
        unsupported(
          featureUnsupported(
            'twap',
            'Synthetix current registration does not support TWAP orders.'
          )
        ),
        unsupportedAdvancedOrderParentLifecycle(
          'Synthetix current registration has no advanced-order parent lifecycle.'
        ),
        'unsupported'
      ),
      twapRandomization: advancedOrderCapability(
        unsupported(
          featureUnsupported(
            'twapRandomization',
            'Synthetix current registration does not support TWAP randomization.'
          )
        ),
        twapRandomizationParentLifecycle(),
        'unsupported'
      ),
    },
    visibility: CURRENT_PROVIDER_DESCRIPTORS.synthetix.visibility,
  },
  lighter: {
    providerId: 'lighter',
    environment: 'production',
    network: 'mainnet',
    markets: perpsOnlyMarkets(
      'current perps app registration is perp-oriented only',
      'current perps app registration has no outcome product'
    ),
    directDeposit: supported({
      route: 'trpc.perps.deposit',
      constraints: [
        'USDC on Arbitrum',
        'EOA wallet only',
        'minimum deposit 10 USD',
      ],
    }),
    swidgeDeposit: supported({
      route: 'trpc.perps.depositWithSwidge',
      constraints: [
        'USDC on Arbitrum destination',
        'EOA wallet only',
        'minimum quoted destination amount 10 USD',
      ],
    }),
    withdraw: supported({
      route: 'trpc.perps.withdraw',
      constraints: [
        'USDC on Arbitrum',
        'requires existing Lighter account',
        'minimum withdrawal 1 USD',
      ],
      metadata: { modes: ['secure', 'fast'] },
    }),
    [INTERNAL_USD_CLASS_MOVEMENT]: notApplicable(
      'Lighter does not expose Hyperliquid USD balance classes.'
    ),
    spotSend: unsupported(
      featureUnsupported(
        'spotSend',
        'Lighter spot send is not part of the current provider action surface.'
      )
    ),
    sendAsset: unsupported(
      featureUnsupported(
        'sendAsset',
        'Lighter generalized asset send is not part of the current provider action surface.'
      )
    ),
    marketOrder: supported(),
    limitOrder: supported(),
    triggerOrder: supported({
      constraints: ['stop-market and stop-limit order entry'],
    }),
    tpSl: supported(),
    reduceOnly: supported(),
    reverse: supported(),
    close: supported(),
    scale: supported(),
    twap: supported(),
    twapRandomization: unsupported(
      featureUnsupported(
        'twapRandomization',
        'Lighter current registration does not support TWAP randomization.'
      )
    ),
    postOnly: supported(),
    cashback: unsupported(
      featureUnsupported(
        'cashback',
        'Lighter current registration does not support cashback metadata.'
      )
    ),
    topOfBook: supported(),
    advancedOrderParentLifecycle: supported({
      constraints: [
        'TWAP parent lifecycle is provider-native; current Scale submission has no durable parent lifecycle',
        'inspect advancedOrders for per-feature ownership',
      ],
    }),
    collateral: [USDC_ARBITRUM],
    limits: {
      minimumDepositUsd: '10',
      minimumWithdrawalUsd: '1',
    },
    riskPolicy: {
      close: supported(),
      reverse: supported(),
      tpSl: supported(),
      reduceOnly: supported(),
      indeterminateActionPolicy: 'requires_review',
      knownProtectionCancelFailurePolicy: 'fail_closed',
    },
    advancedOrders: currentAdvancedOrders(
      supported(),
      supported(),
      unsupported(
        featureUnsupported(
          'twapRandomization',
          'Lighter current registration does not support TWAP randomization.'
        )
      )
    ),
    visibility: CURRENT_PROVIDER_DESCRIPTORS.lighter.visibility,
  },
  mock: {
    providerId: 'mock',
    environment: 'test',
    network: 'testnet',
    markets: {
      perp: supported({
        constraints: ['test helper registration only'],
      }),
      spot: notPerpProduct('marketOrder'),
      outcome: notPerpProduct('marketOrder'),
    },
    directDeposit: testOnlyProvider('mock'),
    swidgeDeposit: testOnlyProvider('mock'),
    withdraw: testOnlyProvider('mock'),
    [INTERNAL_USD_CLASS_MOVEMENT]: testOnlyProvider('mock'),
    spotSend: testOnlyProvider('mock'),
    sendAsset: testOnlyProvider('mock'),
    marketOrder: supported({
      constraints: ['test helper behavior only'],
    }),
    limitOrder: supported({
      constraints: ['test helper behavior only'],
    }),
    triggerOrder: supported({
      constraints: ['test helper behavior only'],
    }),
    tpSl: supported({
      constraints: ['test helper behavior only'],
    }),
    reduceOnly: unsupported(
      featureUnsupported(
        'reduceOnly',
        'mock is not accepted by production perps route schemas.'
      )
    ),
    reverse: supported({
      constraints: ['test helper behavior only'],
    }),
    close: supported({
      constraints: ['test helper behavior only'],
    }),
    scale: supported({
      constraints: ['test helper behavior only'],
    }),
    twap: unsupported(
      featureUnsupported(
        'twap',
        'mock current test helper does not support TWAP orders.'
      )
    ),
    twapRandomization: unsupported(
      featureUnsupported(
        'twapRandomization',
        'mock current test helper does not support TWAP randomization.'
      )
    ),
    postOnly: unsupported(
      featureUnsupported(
        'postOnly',
        'mock current test helper does not support post-only orders.'
      )
    ),
    cashback: unsupported(
      featureUnsupported(
        'cashback',
        'mock current test helper does not support cashback metadata.'
      )
    ),
    topOfBook: unsupported(
      featureUnsupported(
        'topOfBook',
        'mock current test helper does not support top-of-book display.'
      )
    ),
    advancedOrderParentLifecycle: supported({
      constraints: ['scale lifecycle can be represented in tests'],
    }),
    collateral: [],
    limits: {},
    riskPolicy: {
      close: supported(),
      reverse: supported(),
      tpSl: supported(),
      reduceOnly: unsupported(
        featureUnsupported(
          'reduceOnly',
          'mock is not accepted by production perps route schemas.'
        )
      ),
      indeterminateActionPolicy: 'requires_review',
      knownProtectionCancelFailurePolicy: 'fail_closed',
    },
    advancedOrders: {
      scale: advancedOrderCapability(
        supported(),
        supported({
          constraints: ['scale lifecycle can be represented in tests'],
        }),
        'app_orchestrated'
      ),
      twap: advancedOrderCapability(
        unsupported(
          featureUnsupported(
            'twap',
            'mock current test helper does not support TWAP orders.'
          )
        ),
        unsupportedAdvancedOrderParentLifecycle(
          'mock current test helper does not support TWAP parent lifecycle.'
        ),
        'unsupported'
      ),
      twapRandomization: advancedOrderCapability(
        unsupported(
          featureUnsupported(
            'twapRandomization',
            'mock current test helper does not support TWAP randomization.'
          )
        ),
        twapRandomizationParentLifecycle(),
        'unsupported'
      ),
    },
    visibility: CURRENT_PROVIDER_DESCRIPTORS.mock.visibility,
  },
} as const satisfies Record<PerpsProviderId, ProviderCapabilitySetBase>;

const withProductCapabilities = (
  capabilities: Record<PerpsProviderId, ProviderCapabilitySetBase>
): Record<PerpsProviderId, ProviderCapabilitySet> => ({
  hyperliquid: {
    ...capabilities.hyperliquid,
    productCapabilities: productCapabilitiesForProvider(
      capabilities.hyperliquid
    ),
  },
  synthetix: {
    ...capabilities.synthetix,
    productCapabilities: productCapabilitiesForProvider(capabilities.synthetix),
  },
  lighter: {
    ...capabilities.lighter,
    productCapabilities: productCapabilitiesForProvider(capabilities.lighter),
  },
  mock: {
    ...capabilities.mock,
    productCapabilities: productCapabilitiesForProvider(capabilities.mock),
  },
});

export const CURRENT_PROVIDER_CAPABILITIES = withProductCapabilities(
  CURRENT_PROVIDER_CAPABILITIES_BASE
);

export const HYPERLIQUID_EXTENSION_MARKETS = {
  spot: {
    providerId: 'hyperliquid',
    productKind: 'spot',
    symbol: 'PURR/USDC',
    providerMarketId: 'spot:10000',
    assetIdentity: {
      productKind: 'spot',
      spotAssetId: '10000',
      tokenId: '0xc1fb593aeffbeb02f85e0308e9956a90',
      quoteToken: 'USDC',
      quoteScope: 'USDC',
      spotPairIndex: 0,
      signedActionAssetId: 10000,
      protocolCoin: 'PURR/USDC',
      isCanonicalPair: true,
      baseToken: {
        index: 1,
        name: 'PURR',
        fullName: null,
        tokenId: '0xc1fb593aeffbeb02f85e0308e9956a90',
        isCanonical: true,
        szDecimals: 0,
        weiDecimals: 5,
        evmContract: {
          address: '0x9b498c3c8a0b8cd8ba1d9851d40d186f1872b44e',
          evm_extra_wei_decimals: 13,
        },
      },
      quoteTokenMetadata: {
        index: 0,
        name: 'USDC',
        fullName: null,
        tokenId: '0x6d1e7cde53ba9467b783cb7c530ce054',
        isCanonical: true,
        szDecimals: 8,
        weiDecimals: 8,
        evmContract: {
          address: '0x6b9e773128f453f5c2c60935ee2de2cbc5390a24',
          evm_extra_wei_decimals: -2,
        },
      },
      displayLabel: 'PURR/USDC',
      searchKeys: [
        'PURR/USDC',
        'PURR/USDC',
        '0xc1fb593aeffbeb02f85e0308e9956a90',
        '0x6d1e7cde53ba9467b783cb7c530ce054',
      ],
    },
  },
  spotNonUsdc: {
    providerId: 'hyperliquid',
    productKind: 'spot',
    symbol: 'HYPE/USDT0',
    providerMarketId: 'spot:10207',
    assetIdentity: {
      productKind: 'spot',
      spotAssetId: '10207',
      tokenId: '0x0d01dc56dcaaca66ad901c959b4011ec',
      quoteToken: 'USDT0',
      quoteScope: 'non-USDC',
      spotPairIndex: 207,
      signedActionAssetId: 10207,
      protocolCoin: '@207',
      isCanonicalPair: false,
      baseToken: {
        index: 150,
        name: 'HYPE',
        fullName: 'Hyperliquid',
        tokenId: '0x0d01dc56dcaaca66ad901c959b4011ec',
        isCanonical: false,
        szDecimals: 2,
        weiDecimals: 8,
        evmContract: null,
      },
      quoteTokenMetadata: {
        index: 268,
        name: 'USDT0',
        fullName: 'USDT0',
        tokenId: '0x25faedc3f054130dbb4e4203aca63567',
        isCanonical: false,
        szDecimals: 2,
        weiDecimals: 8,
        evmContract: {
          address: '0xb8ce59fc3717ada4c02eadf9682a9e934f625ebb',
          evm_extra_wei_decimals: -2,
        },
      },
      displayLabel: 'HYPE/USDT0',
      searchKeys: [
        '@207',
        'HYPE/USDT0',
        'Hyperliquid',
        'USDT0',
        '0x0d01dc56dcaaca66ad901c959b4011ec',
        '0x25faedc3f054130dbb4e4203aca63567',
      ],
    },
  },
  outcome: {
    providerId: 'hyperliquid',
    productKind: 'outcome',
    symbol: 'Recurring Yes',
    providerMarketId: '#200',
    assetIdentity: {
      productKind: 'outcome',
      hip4AssetId: '#200',
      tokenId: '+200',
      encoding: 200,
      actionAssetId: 100_000_200,
      collateralToken: 'USDC',
      supportIdentifiers: [
        {
          kind: 'hip4_encoding',
          value: '200',
          label: 'encoding = 10 * outcome + side',
        },
        {
          kind: 'protocol_coin',
          value: '#200',
          label: 'trade coin',
        },
        {
          kind: 'base_token_id',
          value: '+200',
          label: 'balance token',
        },
        {
          kind: 'action_asset_id',
          value: '100000200',
          label: '100000000 + encoding',
        },
      ],
    },
  },
} as const satisfies Record<'spot' | 'spotNonUsdc' | 'outcome', MarketIdentity>;

export const HYPERLIQUID_EXTENSION_CAPABILITIES = {
  spot: CURRENT_PROVIDER_CAPABILITIES.hyperliquid.productCapabilities.spot
    .marketDiscovery.marketCatalog,
  outcome:
    CURRENT_PROVIDER_CAPABILITIES.hyperliquid.productCapabilities.outcome
      .marketDiscovery.marketCatalog,
} as const satisfies Record<'spot' | 'outcome', CapabilityDecision>;

export function isCapabilitySupported(
  decision: CapabilityDecision
): decision is Extract<CapabilityDecision, { state: 'supported' }> {
  return decision.state === 'supported';
}

export function isProductionPerpsProviderId(
  providerId: PerpsProviderId
): providerId is ProductionPerpsProviderId {
  return (
    providerId === 'hyperliquid' ||
    providerId === 'synthetix' ||
    providerId === 'lighter'
  );
}

export function getProviderDescriptor(
  providerId: PerpsProviderId
): ProviderDescriptor {
  return CURRENT_PROVIDER_DESCRIPTORS[providerId];
}

export function getProviderCapabilities(
  providerId: PerpsProviderId
): ProviderCapabilitySet {
  return CURRENT_PROVIDER_CAPABILITIES[providerId];
}

export function getMarketCapabilityDecision(
  providerId: PerpsProviderId,
  productKind: MarketProductKind
): CapabilityDecision {
  return CURRENT_PROVIDER_CAPABILITIES[providerId].markets[productKind];
}

function isProviderSpotQuoteScope(
  value: unknown
): value is ProviderSpotQuoteScope {
  return value === 'USDC' || value === 'non-USDC';
}

function getSpotQuoteScope(identity: MarketIdentity): ProviderSpotQuoteScope {
  const assetIdentity = identity.assetIdentity;

  if (assetIdentity && typeof assetIdentity === 'object') {
    const quoteScope = (assetIdentity as Readonly<Record<string, unknown>>)[
      'quoteScope'
    ];
    if (isProviderSpotQuoteScope(quoteScope)) {
      return quoteScope;
    }

    const quoteAsset =
      (assetIdentity as Readonly<Record<string, unknown>>)['quoteAsset'] ??
      (assetIdentity as Readonly<Record<string, unknown>>)['quoteToken'] ??
      (assetIdentity as Readonly<Record<string, unknown>>)['quoteSymbol'];
    if (typeof quoteAsset === 'string') {
      return quoteAsset === 'USDC' ? 'USDC' : 'non-USDC';
    }
  }

  const [, symbolQuote] = identity.symbol.split('/');
  return symbolQuote === undefined || symbolQuote === 'USDC'
    ? 'USDC'
    : 'non-USDC';
}

type ProviderProductCapabilityProfile =
  | ProviderProductCapabilitySet
  | ProviderProductCapabilityQuoteProfile;

function getProductCapabilityProfile(
  capabilities: ProviderCapabilitySet,
  identity: MarketIdentity
): ProviderProductCapabilityProfile {
  const productCapabilities =
    capabilities.productCapabilities[identity.productKind];

  if (identity.productKind !== 'spot') {
    return productCapabilities;
  }

  const quoteScope = getSpotQuoteScope(identity);
  return (
    productCapabilities.quoteOverrides?.[quoteScope] ?? productCapabilities
  );
}

function getProductCapabilityGroups(
  capabilities: ProviderCapabilitySet,
  identity: MarketIdentity
): ProviderProductCapabilityGroups {
  return getProductCapabilityProfile(capabilities, identity);
}

const projectedProductCapabilitySet = (
  currentProductCapabilities: ProviderProductCapabilitySet,
  profile: ProviderProductCapabilityProfile
): ProviderProductCapabilitySet => ({
  ...currentProductCapabilities,
  executionScope: profile.executionScope,
  quoteScope: profile.quoteScope,
  metadata: profile.metadata,
  marketDiscovery: profile.marketDiscovery,
  marketData: profile.marketData,
  accountData: profile.accountData,
  execution: profile.execution,
  advancedOrders: profile.advancedOrders,
  moneyMovement: profile.moneyMovement,
  feesAnalytics: profile.feesAnalytics,
  lifecycleActions: profile.lifecycleActions,
});

function getMarketScopedCapabilities(
  capabilities: ProviderCapabilitySet,
  identity: MarketIdentity
): ProviderCapabilitySet {
  if (identity.productKind === 'perp') {
    return capabilities;
  }

  const profile = getProductCapabilityProfile(capabilities, identity);
  const currentProductCapabilities =
    capabilities.productCapabilities[identity.productKind];
  const advancedOrderParentLifecycle = profile.advancedOrders.parentLifecycle;

  return {
    ...capabilities,
    markets: {
      ...capabilities.markets,
      [identity.productKind]: profile.marketDiscovery.marketCatalog,
    },
    productCapabilities: {
      ...capabilities.productCapabilities,
      [identity.productKind]: projectedProductCapabilitySet(
        currentProductCapabilities,
        profile
      ),
    },
    directDeposit: profile.moneyMovement.directDeposit,
    swidgeDeposit: profile.moneyMovement.swidgeDeposit,
    withdraw: profile.moneyMovement.withdraw,
    [INTERNAL_USD_CLASS_MOVEMENT]:
      profile.moneyMovement[INTERNAL_USD_CLASS_MOVEMENT],
    spotSend: profile.moneyMovement.spotSend,
    sendAsset: profile.moneyMovement.sendAsset,
    marketOrder: profile.execution.marketOrder,
    limitOrder: profile.execution.limitOrder,
    triggerOrder: profile.execution.triggerOrder,
    tpSl: profile.lifecycleActions.tpSl,
    reduceOnly: profile.execution.reduceOnly,
    reverse: profile.lifecycleActions.reverse,
    close: profile.lifecycleActions.close,
    scale: profile.advancedOrders.scale,
    twap: profile.advancedOrders.twap,
    twapRandomization: profile.advancedOrders.twapRandomization,
    postOnly: profile.execution.postOnly,
    cashback: profile.feesAnalytics.cashback,
    topOfBook: profile.marketData.topOfBook,
    advancedOrderParentLifecycle,
    riskPolicy: {
      ...capabilities.riskPolicy,
      close: profile.lifecycleActions.close,
      reverse: profile.lifecycleActions.reverse,
      tpSl: profile.lifecycleActions.tpSl,
      reduceOnly: profile.execution.reduceOnly,
    },
    advancedOrders: {
      scale: {
        ...capabilities.advancedOrders.scale,
        capability: profile.advancedOrders.scale,
        parentLifecycle: advancedOrderParentLifecycle,
      },
      twap: {
        ...capabilities.advancedOrders.twap,
        capability: profile.advancedOrders.twap,
        parentLifecycle: advancedOrderParentLifecycle,
      },
      twapRandomization: {
        ...capabilities.advancedOrders.twapRandomization,
        capability: profile.advancedOrders.twapRandomization,
        parentLifecycle: advancedOrderParentLifecycle,
      },
    },
  };
}

function getProductCapabilityDecision(
  capabilities: ProviderCapabilitySet,
  identity: MarketIdentity,
  capability: ProviderCapabilityName
): CapabilityDecision {
  const groups = getProductCapabilityGroups(capabilities, identity);

  switch (capability) {
    case 'directDeposit':
      return groups.moneyMovement.directDeposit;
    case 'swidgeDeposit':
      return groups.moneyMovement.swidgeDeposit;
    case 'withdraw':
      return groups.moneyMovement.withdraw;
    case INTERNAL_USD_CLASS_MOVEMENT:
      return groups.moneyMovement[INTERNAL_USD_CLASS_MOVEMENT];
    case 'spotSend':
      return groups.moneyMovement.spotSend;
    case 'sendAsset':
      return groups.moneyMovement.sendAsset;
    case 'marketOrder':
      return groups.execution.marketOrder;
    case 'limitOrder':
      return groups.execution.limitOrder;
    case 'triggerOrder':
      return groups.execution.triggerOrder;
    case 'tpSl':
      return groups.lifecycleActions.tpSl;
    case 'reduceOnly':
      return groups.execution.reduceOnly;
    case 'reverse':
      return groups.lifecycleActions.reverse;
    case 'close':
      return groups.lifecycleActions.close;
    case 'scale':
      return groups.advancedOrders.scale;
    case 'twap':
      return groups.advancedOrders.twap;
    case 'twapRandomization':
      return groups.advancedOrders.twapRandomization;
    case 'postOnly':
      return groups.execution.postOnly;
    case 'cashback':
      return groups.feesAnalytics.cashback;
    case 'topOfBook':
      return groups.marketData.topOfBook;
    case 'advancedOrderParentLifecycle':
      return groups.advancedOrders.parentLifecycle;
  }
}

export function requireProviderCapability(
  providerId: PerpsProviderId,
  capability: ProviderCapabilityName
): CapabilityDecision {
  return CURRENT_PROVIDER_CAPABILITIES[providerId][capability];
}

export const currentPerpsProviderRegistry: PerpsProviderRegistry = {
  getProvider(providerId) {
    return getProviderCapabilities(providerId);
  },
  getMarket(identity) {
    const capabilities = getProviderCapabilities(identity.providerId);

    return {
      identity,
      capabilities: getMarketScopedCapabilities(capabilities, identity),
    };
  },
  requireCapability(identity, capability) {
    const capabilities = getProviderCapabilities(identity.providerId);
    const productDecision = getProductCapabilityDecision(
      capabilities,
      identity,
      capability
    );

    if (identity.productKind !== 'perp') {
      return productDecision;
    }

    const marketDecision = getMarketCapabilityDecision(
      identity.providerId,
      identity.productKind
    );

    if (marketDecision.state !== 'supported') {
      return marketDecision;
    }

    return productDecision;
  },
};

export function describeCapabilityDecision(
  decision: CapabilityDecision
): string {
  if (decision.state === 'supported') {
    return 'supported';
  }

  return decision.reason;
}
